import { useEffect, useState } from "react";
import { Modal } from "@components/ui/Modal";
import { Button } from "@components/ui/Button";

export interface OrderFiltersState {
  status: string;
  gateway: string;
}

interface OrderFiltersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  filters: OrderFiltersState;
  onApply: (filters: OrderFiltersState) => void;
}

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "cancelled", label: "Cancelled" },
  { value: "expired", label: "Expired" },
  { value: "refunded", label: "Refunded" },
];

const GATEWAY_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "All Gateways" },
  { value: "paytabs", label: "PayTabs" },
  { value: "legacy", label: "Legacy" },
];

export function OrderFiltersDialog({
  isOpen,
  onClose,
  filters,
  onApply,
}: OrderFiltersDialogProps) {
  const [localFilters, setLocalFilters] = useState<OrderFiltersState>(filters);

  // Sync local state when the dialog opens so the user sees the current selection.
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters, isOpen]);

  if (!isOpen) return null;

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleReset = () => {
    const reset: OrderFiltersState = { status: "all", gateway: "all" };
    setLocalFilters(reset);
    onApply(reset);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Filter Orders"
      className="max-w-md"
    >
      <div className="space-y-4">
        <div>
          <label
            htmlFor="order-status-filter"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Status
          </label>
          <select
            id="order-status-filter"
            value={localFilters.status}
            onChange={(e) =>
              setLocalFilters({ ...localFilters, status: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="order-gateway-filter"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Gateway
          </label>
          <select
            id="order-gateway-filter"
            value={localFilters.gateway}
            onChange={(e) =>
              setLocalFilters({ ...localFilters, gateway: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {GATEWAY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-3 pt-6 border-t border-gray-100 mt-6">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button
            variant="primary"
            className="flex-1"
            onClick={handleApply}
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </Modal>
  );
}
